import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Switch,
  Input,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { SINISTER_RESPONSIBLE_OPTIONS } from 'common/src/utils/sinister';
import { useEffect, useState } from 'react';
import { updateSettlement } from 'common/src/api/settlements';
import { addHistoryComment } from '../../services/historyService';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface SinisterSettlementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  settlement: Settlement | null;
  onUpdate: (
    id: number,
    isSinister: boolean,
    responsible?: string | null,
    delayDays?: number | null,
    caseNumber?: string | null
  ) => void;
}

export default function SinisterSettlementDialog({
  isOpen,
  onClose,
  settlement,
  onUpdate,
}: SinisterSettlementDialogProps) {
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [responsible, setResponsible] = useState<string>('');
  const [hasDelay, setHasDelay] = useState(false);
  const [delayDays, setDelayDays] = useState<string>('');
  const [caseNumber, setCaseNumber] = useState<string>('');
  const { userProfile } = useUserStore();

  useEffect(() => {
    if (isOpen) {
      setResponsible(settlement?.sinister_responsible || '');
      const existingDelay = settlement?.sinister_delay_days || 0;
      setHasDelay(existingDelay > 0);
      setDelayDays(existingDelay > 0 ? String(existingDelay) : '');
      setCaseNumber(settlement?.sinister_case_number || '');
    }
  }, [isOpen, settlement]);

  const isMarking = !settlement?.is_sinister;
  const parsedDelay = Number(delayDays);
  const delayInvalid =
    hasDelay && (!delayDays || isNaN(parsedDelay) || parsedDelay <= 0);
  const disableSubmit = isMarking && (!responsible || delayInvalid);

  const handleToggleSinister = async () => {
    if (!settlement || !userProfile) return;
    if (disableSubmit) return;

    setIsLoading(true);
    try {
      const newIsSinister = !settlement.is_sinister;
      const newResponsible = newIsSinister ? responsible : null;
      const newDelayDays = newIsSinister && hasDelay ? parsedDelay : null;
      const newCaseNumber =
        newIsSinister && caseNumber.trim() ? caseNumber.trim() : null;

      await updateSettlement(settlement.id, {
        is_sinister: newIsSinister,
        sinister_responsible: newResponsible,
        sinister_delay_days: newDelayDays,
        sinister_case_number: newCaseNumber,
      });

      const delayNote = newDelayDays
        ? ` con retraso de ${newDelayDays} días`
        : newIsSinister
        ? ' sin retraso'
        : '';
      const actionMessage = `${userProfile.first_name} ${
        userProfile.last_name
      } ha ${
        newIsSinister
          ? `marcado como siniestrada (responsable: ${newResponsible}${delayNote})`
          : 'marcado como no siniestrada'
      } la liquidación`;
      await addHistoryComment(
        settlement.id,
        actionMessage,
        userProfile.id,
        'action'
      );

      onUpdate(
        settlement.id,
        newIsSinister,
        newResponsible,
        newDelayDays,
        newCaseNumber
      );
      onClose();
    } catch (error) {
      console.error('Error updating settlement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          {settlement?.is_sinister
            ? 'Desmarcar como Siniestro'
            : 'Marcar como Siniestro'}
        </ModalHeader>
        <ModalBody>
          <p>
            {settlement?.is_sinister
              ? '¿Estás seguro que deseas quitar la marca de siniestro de esta liquidación?'
              : 'Al marcarla como siniestrada, podrás valorizar el perjuicio en el módulo de Siniestros.'}
          </p>
          {isMarking && (
            <>
              <Select
                label={t('Responsable del siniestro')}
                placeholder={t('Selecciona un responsable')}
                selectedKeys={responsible ? [responsible] : []}
                onChange={(e) => setResponsible(e.target.value)}
                isRequired
                className='mt-2'
              >
                {SINISTER_RESPONSIBLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {t(opt)}
                  </SelectItem>
                ))}
              </Select>

              <div className='mt-3 flex items-center justify-between'>
                <div>
                  <div className='text-sm font-medium'>
                    ¿El siniestro generó retraso en la venta?
                  </div>
                  <div className='text-xs text-default-500'>
                    Mide los días que la venta se postergó respecto a cuándo
                    debió ocurrir. Sin retraso → comparamos contra la fecha de
                    venta. Con retraso → comparamos contra (fecha de venta − N
                    días), es decir el día en que se habría vendido sin el
                    siniestro.
                  </div>
                </div>
                <Switch isSelected={hasDelay} onValueChange={setHasDelay} />
              </div>

              {hasDelay && (
                <Input
                  type='number'
                  min={1}
                  label='Días de retraso (vs fecha de venta original)'
                  placeholder='Ej: 5'
                  description='Días que el siniestro postergó la venta. NO es retraso de ETA de la nave.'
                  value={delayDays}
                  onChange={(e) => setDelayDays(e.target.value)}
                  isInvalid={delayInvalid}
                  errorMessage={
                    delayInvalid ? 'Ingresa un número mayor a 0' : undefined
                  }
                  className='mt-2'
                />
              )}

              <Input
                label={t('Número de Caso (opcional)')}
                placeholder={t('ID interno de la exportadora')}
                value={caseNumber}
                onChange={(e) => setCaseNumber(e.target.value)}
                className='mt-2'
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            Cancelar
          </Button>
          <Button
            color='primary'
            onPress={handleToggleSinister}
            isLoading={isLoading}
            isDisabled={disableSubmit}
          >
            {settlement?.is_sinister ? 'Desmarcar' : 'Marcar como Siniestro'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
